import { Icon } from '@iconify/react';
import { Button, Input, Select, Textarea, SelectItem, Slider } from '@nextui-org/react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, forwardRef, useImperativeHandle } from 'react';
import { ListValueSelect } from '../utiluicomponents/ListValueSelect';

export enum confInputType {
    SINGLE_SELECT = 'single-select',
    MULTI_SELECT = 'multi-select',
    TEXT = 'text',
    TEXTAREA = 'textarea',
    NUMBER = 'number',
    EMAIL = 'email',
    INPUT_LIST = 'input-list',
    SLIDER = 'slider',
}

export interface ConfInput {
    label?: string;
    id: string;
    value?: string | number | string[] | number[];
    required: boolean;
    disabled?: boolean;
    placeholder?: string;
    inputType?: confInputType;
    validation?: string;
    canCopy?: boolean;
    options?: { label: string; value: string; }[];
    listLimit?: number;
    field_details?: string;
    minRows?: number;
    maxRows?: number;
    minValue?: number;
    maxValue?: number;
    style?: string;
}


export interface GlobalConfigurationProps {
    inputs: ConfInput[];
    formAlignmentStyle?: string;
    formStyle?: string;
    footerItems?: () => JSX.Element;
    onChange?: (fieldName: string, value: any) => void;
    onSave?: (values: Record<string, any>) => void;
    formRef?: React.RefObject<HTMLDivElement>;
}

const getDefaultFieldValue = (input: ConfInput) =>
    input.value ?? (input.inputType === confInputType.MULTI_SELECT ? [] : '');

export const GlobalConfiguration = forwardRef(function GlobalConfiguration(props: Readonly<GlobalConfigurationProps>, ref) {
    const { inputs, formAlignmentStyle, formStyle = 'p-3', footerItems } = props;
    const { t } = useTranslation();

    const defaultValues = inputs?.reduce(
        (acc, curr) => ({ ...acc, [curr.id]: getDefaultFieldValue(curr) }),
        {}
    );

    interface FormData {
        [key: string]: string | string[] | number | number[];
    }

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues,
    });

    useEffect(() => {
        const currentValues = watch();
        const updatedDefaults: Record<string, any> = {};

        inputs.forEach((input) => {
            const currentValue = currentValues[input.id];
            const isMultiSelect = input.inputType === confInputType.MULTI_SELECT;
            const isEmpty =
                currentValue === '' ||
                currentValue === undefined ||
                (isMultiSelect && Array.isArray(currentValue) && currentValue.length === 0);
            if (isEmpty) {
                updatedDefaults[input.id] = getDefaultFieldValue(input);
            }
        });

        if (Object.keys(updatedDefaults).length > 0) {
            reset({ ...currentValues, ...updatedDefaults });
        }
    }, [inputs]);


    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name && props.onChange) {
                props.onChange(name, value[name]);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, props]);

    useImperativeHandle(ref, () => ({
        validateAndSubmit: () => {
            return new Promise((resolve, reject) => {
                handleSubmit(
                    (data: FormData) => {
                        resolve(data);
                    },
                    (errors) => {
                        reject(errors);
                    }
                )();
            });
        },
        resetForm: () => {
            reset(defaultValues);
        }
    }));

    const CopyToClipBoard: React.FC<{ field: string, canView: boolean; }> = ({ field, canView }) => {

        const handleCopy = (text: string) => {
            navigator.clipboard.writeText(text);
        };

        if (!canView) return null;

        const fieldValue = watch(field);
        return (
            <Button
                variant="light"
                onClick={() => handleCopy(String(fieldValue ?? ''))}
                startContent={<Icon icon="hugeicons:copy-01" className="text-foreground" height={25} width={25} />}
                className='border-l rounded-none'
            >
                Copy
            </Button>
        );
    };

    return (
        <div className='flex flex-col'>
            <div className={`flex flex-col gap-3 ${formStyle}`}>
                {inputs.map((input) => (
                    <div key={input.id} className={`flex ${formAlignmentStyle}`}>
                        <label htmlFor={input.id} className="flex flex-col w-[30%]">
                            <div>
                                <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>{t(`${input.label}`)}</span>
                                {input.required && <span className="text-danger">*</span>}
                            </div>
                            <div className='font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]'>{t(`${input?.field_details ?? ''}`)}</div>
                        </label>
                        <div className='flex-1'>
                            <Controller
                                name={`${input.id}`}
                                control={control}
                                rules={{
                                    required: input.inputType === confInputType.MULTI_SELECT ? false : input?.required,
                                    min: input?.minValue,
                                    max: input?.maxValue,
                                    validate: input.inputType === confInputType.MULTI_SELECT && input.required
                                        ? (value) => (Array.isArray(value) && value.length > 0) || 'This field is required'
                                        : undefined,
                                }}
                                render={({ field }) => {
                                    switch (input.inputType) {
                                        case confInputType.TEXT:
                                        case confInputType.EMAIL:
                                        case confInputType.NUMBER:
                                            return (
                                                <Input
                                                    id={input.id}
                                                    type={input.inputType}
                                                    disabled={input.disabled}
                                                    className={`${input.style}`}
                                                    {...field}
                                                    value={String(field.value ?? '')}
                                                    endContent={
                                                        <CopyToClipBoard field={input.id} canView={!!input.canCopy} />
                                                    }
                                                />
                                            );
                                        case confInputType.TEXTAREA:
                                            return (
                                                <Textarea
                                                    id={input.id}
                                                    disabled={input.disabled}
                                                    {...field}
                                                    value={String(field.value ?? '')}
                                                    minRows={input.minRows ?? 3}
                                                    maxRows={input.maxRows ?? 5}
                                                />
                                            );
                                        case confInputType.SINGLE_SELECT:
                                            return (
                                                <Select
                                                    size="sm"
                                                    selectedKeys={[String(field.value ?? '')]}
                                                    disallowEmptySelection={true}
                                                    onSelectionChange={(keys) => {
                                                        const security = Array.from(keys)[0];
                                                        field.onChange(security);
                                                    }}
                                                    isDisabled={input.disabled}
                                                    aria-label="advisor-id"
                                                    id="advisor-id"
                                                    labelPlacement='outside'
                                                    placeholder='select a advisor Id'
                                                >
                                                    {(input.options ?? []).map((role) => (
                                                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                                                    ))}
                                                </Select>

                                            );
                                        case confInputType.MULTI_SELECT: {
                                            const selectedValues = Array.isArray(field.value)
                                                ? (field.value as string[])
                                                : [];
                                            return (
                                                <ListValueSelect
                                                    id={input.id}
                                                    options={input.options ?? []}
                                                    value={selectedValues}
                                                    onChange={field.onChange}
                                                    placeholder={input.placeholder ?? 'Select options'}
                                                    ariaLabel={input.label ?? input.id}
                                                    isDisabled={input.disabled}
                                                />
                                            );
                                        }
                                        case confInputType.INPUT_LIST:
                                            return (
                                                <div className="flex flex-col gap-2">
                                                    {new Array(input?.listLimit ?? 0)
                                                        .fill('')
                                                        .map((_, index: number) => (
                                                            <Controller
                                                                key={`${input.id}-${index}`}
                                                                name={`${input.id}[${index}]`}
                                                                control={control}
                                                                rules={{ required: input.required }}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        id={`${input.id}-${index}`}
                                                                        type="text"
                                                                        {...field}
                                                                    />
                                                                )}
                                                            />
                                                        ))}
                                                </div>
                                            );
                                        case confInputType.SLIDER:
                                            return (
                                                <div className="flex flex-col gap-2">
                                                    <Slider
                                                        id={input.id}
                                                        showTooltip={true}
                                                        aria-label={`${input.id}`}
                                                        className="max-w-md"
                                                        disableThumbScale={true}
                                                        value={field.value as any}
                                                        size="md"
                                                        step={1}
                                                        minValue={input.minValue}
                                                        maxValue={input.maxValue}
                                                        onChange={(value) => field.onChange(value)}
                                                    />
                                                    <p className="text-default-500 font-medium text-small">
                                                        Selected budget: {Array.isArray(watch(`${input.id}`)) && watch(`${input.id}`)?.map((b: number) => `${b}`).join(" – ")}
                                                    </p>
                                                </div>
                                            );
                                        default:
                                            return <div>Unsupported input type</div>;
                                    }
                                }}
                            />
                            {(errors[input.id]?.type === 'required' || errors[input.id]?.type === 'validate') && (
                                <span className='text-sm text-danger'>
                                    {typeof errors[input.id]?.message === 'string' ? errors[input.id]?.message : 'This field is required'}
                                </span>
                            )}
                            {errors[input.id]?.type === 'min' && (
                                <span className='text-sm text-danger'>Minimum value is {input.minValue}</span>
                            )}
                            {errors[input.id]?.type === 'max' && (
                                <span className='text-sm text-danger'>Maximum value is {input.maxValue}</span>
                            )}
                        </div>
                    </div>
                ))}
                {footerItems?.()}
            </div>
        </div>
    );
});



export default GlobalConfiguration;